import { MouseEvent } from "react";
import { Link as RouterLink } from "react-router-dom";

import LaunchIcon from "@mui/icons-material/Launch";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import type { SxProps, Theme } from "@mui/material/styles";

import type { CallContact } from "services/dialstack";

import { contactPath } from "./contact-kind.util";
import useContactPrivacySx from "./use-contact-privacy.hook";

// Stops the click from also selecting the clickable row/card that hosts it.
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

// A contact's name as a link into the Rolodex (ENG-2177) — the counterpart
// of the patient-name links every caller surface already has. A family
// contact's name blurs in privacy mode; the launch icon stays so the link is
// still found.
export function ContactNameLink({
  contact,
  fontSize = 16,
  fontWeight = 600,
  color,
  testId = "contact-link"
}: {
  contact: CallContact;
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  testId?: string;
}) {
  const privacySx = useContactPrivacySx(contact.kind);
  return (
    <Link
      component={RouterLink}
      to={contactPath(contact.id)}
      underline="hover"
      onClick={stopPropagation}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize,
        fontWeight,
        width: "fit-content",
        maxWidth: "100%",
        ...(color ? { color } : {})
      }}
      data-testid={testId}
    >
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...privacySx
        }}
      >
        {contact.name}
      </Box>
      <LaunchIcon
        sx={{ fontSize: Math.max(12, fontSize - 4), flexShrink: 0 }}
      />
    </Link>
  );
}

export function OpenContactButton({
  contact,
  sx,
  size = "small"
}: {
  contact: CallContact;
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
}) {
  return (
    <Button
      component={RouterLink}
      to={contactPath(contact.id)}
      variant="outlined"
      size={size}
      onClick={stopPropagation}
      sx={sx}
      data-testid="open-contact"
    >
      Open contact
    </Button>
  );
}
